/**
 * Performance metrics calculation for backtest results
 */
import {
  BacktestPortfolio,
  BacktestOptions,
  BacktestResult,
  BacktestPerformance,
  BacktestTrade
} from './types.js';

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const TRADING_DAYS_PER_YEAR = 252;

/**
 * Calculate comprehensive performance metrics from backtest results
 */
export function calculatePerformanceMetrics(
  portfolio: BacktestPortfolio,
  options: BacktestOptions
): BacktestResult {
  const { equity, trades, initialCapital } = portfolio;
  
  if (equity.length < 2) {
    throw new Error('Insufficient equity data points for performance calculation');
  }
  
  // Calculate basic metrics
  const initialValue = initialCapital;
  const finalValue = equity[equity.length - 1].value;
  
  const totalReturn = (finalValue - initialValue) / initialValue;
  const annualizedReturn = calculateAnnualizedReturn(totalReturn, options.startDate, options.endDate);
  const maxDrawdown = calculateMaxDrawdown(equity);
  const { winRate, profitFactor, averageProfitPerTrade, averageLossPerTrade, averageTradeDuration } = 
    calculateTradeMetrics(trades);
  
  const sharpeRatio = calculateSharpeRatio(equity, initialValue);
  
  // Calculate drawdown series
  const drawdownSeries = calculateDrawdownSeries(equity);
  
  // Calculate monthly returns
  const monthlyReturns = calculateMonthlyReturns(equity);
  
  // Assemble final result
  const performance: BacktestPerformance = {
    totalReturn,
    annualizedReturn,
    maxDrawdown,
    sharpeRatio,
    trades: trades.length,
    winRate,
    profitFactor,
    averageProfitPerTrade,
    averageLossPerTrade,
    averageTradeDuration
  };
  
  return {
    performance,
    equityCurve: equity,
    trades,
    drawdownSeries,
    monthlyReturns
  };
}

/**
 * Calculate annualized return based on total return and date range
 */
function calculateAnnualizedReturn(
  totalReturn: number, 
  startDate: string, 
  endDate: string
): number {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const daysDiff = (end.getTime() - start.getTime()) / MS_PER_DAY;
  
  if (daysDiff <= 0) return 0;
  
  // Use trading days for more accurate calculation
  const tradingDaysFraction = daysDiff / 365 * TRADING_DAYS_PER_YEAR / TRADING_DAYS_PER_YEAR;
  
  return Math.pow(1 + totalReturn, 1 / tradingDaysFraction) - 1;
}

/**
 * Calculate maximum drawdown from equity curve
 */
function calculateMaxDrawdown(equity: Array<{date: string; value: number}>): number {
  let maxDrawdown = 0;
  let peak = equity[0].value;
  
  for (const point of equity) {
    if (point.value > peak) {
      peak = point.value;
    } else {
      const drawdown = (peak - point.value) / peak;
      maxDrawdown = Math.max(maxDrawdown, drawdown);
    }
  }
  
  return maxDrawdown;
}

/**
 * Calculate drawdown series for visualization
 */
function calculateDrawdownSeries(equity: Array<{date: string; value: number}>): Array<{date: string; drawdown: number}> {
  const drawdownSeries = [];
  let peak = equity[0].value;
  
  for (const point of equity) {
    if (point.value > peak) {
      peak = point.value;
    }
    
    const drawdown = peak > 0 ? (peak - point.value) / peak : 0;
    
    drawdownSeries.push({
      date: point.date,
      drawdown: drawdown * 100 // Convert to percentage
    });
  }
  
  return drawdownSeries;
}

/**
 * Calculate monthly returns
 */
function calculateMonthlyReturns(equity: Array<{date: string; value: number}>): Array<{month: string; return: number}> {
  // Group equity points by month
  const monthlyData = new Map<string, {start: number; end: number}>();
  
  for (const point of equity) {
    const date = new Date(point.date);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    
    if (!monthlyData.has(monthKey)) {
      monthlyData.set(monthKey, { start: point.value, end: point.value });
    } else {
      const data = monthlyData.get(monthKey)!;
      data.end = point.value; // Update end value as we process more data points
    }
  }
  
  // Calculate return for each month
  const monthlyReturns = Array.from(monthlyData.entries()).map(([month, data]) => {
    const monthReturn = data.start > 0 ? (data.end - data.start) / data.start * 100 : 0;
    return { month, return: monthReturn };
  });
  
  // Sort chronologically
  return monthlyReturns.sort((a, b) => a.month.localeCompare(b.month));
}

/**
 * Calculate Sharpe ratio
 */
function calculateSharpeRatio(
  equity: Array<{date: string; value: number}>, 
  initialValue: number
): number {
  if (equity.length < 3) return 0; // Need at least 3 points for meaningful calculation
  
  // Calculate daily returns
  const dailyReturns: number[] = [];
  
  for (let i = 1; i < equity.length; i++) {
    const prevValue = equity[i-1].value;
    const currentValue = equity[i].value;
    const dailyReturn = prevValue > 0 ? (currentValue - prevValue) / prevValue : 0;
    dailyReturns.push(dailyReturn);
  }
  
  // Calculate average daily return
  const avgDailyReturn = dailyReturns.reduce((sum, ret) => sum + ret, 0) / dailyReturns.length;
  
  // Calculate daily return standard deviation
  const sumSquaredDiff = dailyReturns.reduce((sum, ret) => {
    const diff = ret - avgDailyReturn;
    return sum + diff * diff;
  }, 0);
  
  const stdDev = Math.sqrt(sumSquaredDiff / dailyReturns.length);
  if (stdDev === 0) return 0; // Avoid division by zero
  
  // Calculate annualized Sharpe ratio (assuming risk-free rate = 0)
  // Multiply by sqrt(252) to annualize
  return avgDailyReturn / stdDev * Math.sqrt(TRADING_DAYS_PER_YEAR);
}

/**
 * Calculate trade metrics including win rate, profit factor, etc.
 */
function calculateTradeMetrics(trades: BacktestTrade[]): {
  winRate: number;
  profitFactor: number;
  averageProfitPerTrade: number;
  averageLossPerTrade: number;
  averageTradeDuration: number;
} {
  if (trades.length === 0) {
    return {
      winRate: 0,
      profitFactor: 0,
      averageProfitPerTrade: 0,
      averageLossPerTrade: 0,
      averageTradeDuration: 0
    };
  }
  
  // Count winning and losing trades
  const winningTrades = trades.filter(t => t.profit > 0);
  const losingTrades = trades.filter(t => t.profit <= 0);
  
  const winRate = trades.length > 0 ? winningTrades.length / trades.length : 0;
  
  // Calculate gross profit and loss
  const grossProfit = winningTrades.reduce((sum, t) => sum + t.profit, 0);
  const grossLoss = Math.abs(losingTrades.reduce((sum, t) => sum + t.profit, 0));
  
  // Calculate profit factor (avoid division by zero)
  const profitFactor = grossLoss > 0 ? grossProfit / grossLoss : grossProfit > 0 ? 999 : 0;
  
  // Calculate average profit per winning trade
  const averageProfitPerTrade = winningTrades.length > 0 
    ? grossProfit / winningTrades.length 
    : 0;
  
  // Calculate average loss per losing trade
  const averageLossPerTrade = losingTrades.length > 0 
    ? grossLoss / losingTrades.length 
    : 0;
  
  // Calculate average trade duration
  let totalDurationDays = 0;
  for (const trade of trades) {
    const entryDate = new Date(trade.entryDate);
    const exitDate = new Date(trade.exitDate);
    const durationDays = (exitDate.getTime() - entryDate.getTime()) / MS_PER_DAY;
    totalDurationDays += durationDays;
  }
  
  const averageTradeDuration = trades.length > 0 ? totalDurationDays / trades.length : 0;
  
  return {
    winRate,
    profitFactor,
    averageProfitPerTrade,
    averageLossPerTrade,
    averageTradeDuration
  };
}